import { Link } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { InfoHint, InfoHintLead, InfoHintSection } from '@/components/InfoHint/InfoHint';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useAuthStore, usePermission } from '@/stores/auth';
import { t } from '@/lib/i18n';
import { useInfoHintPulse } from '@/features/onboarding';
import { useJobsList } from '@/features/jobs';
import { useMembers } from '../hooks';
import { memberStatus } from '../utils';
import { MemberCard } from './MemberCard';
import styles from './MemberRoster.module.css';

export function MemberRoster() {
  const canHire = useAuthStore((s) => s.permissions.has('manage_members'));
  const canCreateJob = usePermission('create_job');
  const { data, isLoading, isError, refetch } = useMembers();
  const jobsQuery = useJobsList();
  const pulsing = useInfoHintPulse();

  const allJobs = jobsQuery.data?.items ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('Your team')}
        subtitle={t("Everyone on your team, and what they're on right now.")}
        action={
          canHire ? (
            <Link to="/team/add" className={styles.addLink}>
              <Button variant="primary" size="sm">
                {t('Add a Member')}
              </Button>
            </Link>
          ) : null
        }
        titleAdornment={
          <InfoHint
            label={t('Team')}
            tooltip={t('Your team. Click a Member to chat or assign work.')}
            pulsing={pulsing}
            modal={{
              title: t('Your Team'),
              body: (
                <>
                  <InfoHintLead>
                    {t(
                      'Your team is a roster of AI specialists, each added for a specific role. They work independently — you assign the job, they deliver the result.',
                    )}
                  </InfoHintLead>
                  <InfoHintSection icon="👤" heading={t('Click any member')}>
                    {t(
                      "Open their profile to see their role, what they're currently working on, and their full job history. You can start a chat or assign a new job directly from there.",
                    )}
                  </InfoHintSection>
                  <InfoHintSection icon="💬" heading={t('Chat to brief or redirect')}>
                    {t(
                      "Use Chat to give a member instructions in plain English — describe what you need and they'll get started. Great for one-off requests that don't fit a template.",
                    )}
                  </InfoHintSection>
                  <InfoHintSection icon="➕" heading={t('Add a Member')}>
                    {t(
                      'Browse the catalog to add a new specialist to your team. Pick a role, give them a name and a brief, and they are ready to work.',
                    )}
                  </InfoHintSection>
                </>
              ),
            }}
          />
        }
      />

      {isLoading ? (
        <div className={styles.empty} role="status" aria-live="polite">
          {t('Loading your team…')}
        </div>
      ) : isError ? (
        <div className={styles.empty} role="alert">
          <p>{t("Couldn't load your team.")}</p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t('Try again')}
          </Button>
        </div>
      ) : data && data.items.length > 0 ? (
        <ul className={styles.grid}>
          {data.items.map((m) => {
            const jobsRunning = allJobs.filter(
              (j) => j.assigned_member.id === m.id && j.status === 'running',
            ).length;
            const status = m.kind === 'ai' ? memberStatus(m, allJobs) : null;
            return (
              <li key={m.id}>
                <MemberCard
                  member={m}
                  status={status}
                  jobsRunning={jobsRunning}
                  canCreateJob={canCreateJob}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={styles.empty}>
          <h2 className={styles.emptyHeading}>{t('No one on the team yet')}</h2>
          <p className={styles.emptyBody}>
            {t('Add your first AI Member from the catalog and brief them on what to do.')}
          </p>
          {canHire ? (
            <Link to="/team/add" className={styles.addLink}>
              <Button variant="primary" size="sm">
                {t('Add your first Member')}
              </Button>
            </Link>
          ) : null}
        </div>
      )}
    </div>
  );
}
